// src/components/ads/MediavineProSync.tsx
import { useEffect } from 'react'
import { useProStatus } from '../../api/proStatus'
import { useSupabaseToken } from '../../hooks/useSupabaseToken'
import { isAndroidApp } from '../../lib/androidApp'
import { handoffFieldsBusy } from '../../lib/prerenderHandoff'
import {
  ADS_KILL,
  PRO_RELOAD_DEADLINE_MS,
  adsBlockedAtBoot,
  adsHaveLoaded,
  adsOffDecision,
  applyAdsBlocklist,
  clearProReloadMarker,
  msSinceBoot,
  readAdsOffFlag,
  readAndroidReloadMarker,
  readProReloadMarker,
  reloadPage,
  shouldReloadForPro,
  userHasInteracted,
  writeAdsOffFlag,
  writeAndroidReloadMarker,
  writeProReloadMarker,
} from '../../lib/ads'

/**
 * Keeps the Pro ad suppression in step with the entitlement. Renders nothing
 * and mounts once, at the app root next to GA4PageViews, because it is about
 * the whole page and not any one route.
 *
 * The Mediavine wrapper is static and async, so "do not load it for Pro" is
 * not on the table. The lever is data-blocklist-all on the settings div, and
 * for it to count at first paint it has to come from a cached flag that the
 * inline script in index.html reads before the bundle boots. This component
 * is what writes that flag: once the session and /pro/status have resolved it
 * caches the answer for the next visit AND applies it to the live div for
 * this one. The decision table itself is `adsOffDecision`, pure and tested on
 * its own.
 *
 * Applying it to the live div is NOT enough once ads have filled: Journey
 * reads the div at first paint only and SPA routes never reload it (verified
 * live 2026-10-05, a signed-in Pro saw ads all session on a device with no
 * cached flag). So when the answer is Pro this may do ONE reload after
 * writing the flag, so the inline script starts the next load blocked - but
 * only on the narrow terms of `shouldReloadForPro`: ads are on the page, the
 * page did not start blocked, the answer came within the boot deadline, the
 * reader has not touched anything yet (index.html's inline sentinel covers
 * the prerendered fields before this bundle boots), and no prerender handoff
 * field holds text or focus. Ads that show up later just leave
 * the flag for the next load; there is no watcher. A per-user sessionStorage
 * marker caps it at one reload per tab per account, and a non-Pro answer
 * clears it so a Pro -> signed out -> Pro cycle can reload again.
 *
 * Inside our Android app (isAndroidApp) there are never ads, for anyone: it
 * only keeps the live div blocked and returns before ANY flag write, marker
 * or reload. It must never write cb.adsOff for that reason: the Trusted Web
 * Activity shares Chrome's storage for this origin, so the flag would also
 * strip ads from the reader's regular Chrome browsing. Nor does it clear an
 * existing one: a Pro's cached flag has to survive the app session. The
 * index.html inline script blocks first paint in-app on its own; if ads
 * filled anyway (the wrapper won the race), one guarded reload per tab
 * (ANDROID_ADS_RELOADED_KEY, sessionStorage) starts the page over blocked.
 */
export default function MediavineProSync() {
  const { userId, loading } = useSupabaseToken()
  const { status, data } = useProStatus()
  const isPro = data?.is_pro
  useEffect(() => {
    if (isAndroidApp()) {
      applyAdsBlocklist(true)
      // Recovery for the boot race: index.html's block runs after the async
      // wrapper tag, so a cached wrapper could fill first. If ads are on the
      // page, reload ONCE per tab so the next load starts blocked. The
      // sessionStorage marker is the only loop guard: no marker, no reload.
      // Same courtesy as the Pro reload: never throw away what the reader typed.
      const recover = (): boolean => {
        if (adsHaveLoaded() && !userHasInteracted() && !handoffFieldsBusy() &&
          !readAndroidReloadMarker() && writeAndroidReloadMarker()) {
          reloadPage()
          return true
        }
        return false
      }
      if (recover()) return
      // Ads can also appear AFTER this effect ran (the wrapper filled late).
      // Watch the DOM for them until the same boot deadline the Pro reload
      // uses; past it a reload costs the reader more than the ad does.
      const remaining = PRO_RELOAD_DEADLINE_MS - msSinceBoot()
      if (adsHaveLoaded() || remaining <= 0 || typeof MutationObserver === 'undefined') return
      const observer = new MutationObserver(() => {
        if (!adsHaveLoaded()) return
        observer.disconnect()
        recover()
      })
      // eslint-disable-next-line i18next/no-literal-string -- DOM attribute name, not copy
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
      const timer = window.setTimeout(() => observer.disconnect(), remaining)
      return () => {
        observer.disconnect()
        window.clearTimeout(timer)
      }
    }
    const off = adsOffDecision({ loading, userId, status, isPro })
    if (off === null) return
    writeAdsOffFlag(off)
    applyAdsBlocklist(off)
    if (off !== true) {
      clearProReloadMarker()
      return
    }
    // off === true implies a signed-in user (adsOffDecision), so userId is set.
    const uid = userId ?? ''
    const reload = shouldReloadForPro({
      off,
      kill: ADS_KILL,
      bootBlocked: adsBlockedAtBoot(),
      adsLoaded: adsHaveLoaded(),
      alreadyReloaded: readProReloadMarker(uid),
      flagPersisted: readAdsOffFlag(),
      elapsedMs: msSinceBoot(),
      interacted: userHasInteracted(),
      formBusy: handoffFieldsBusy(),
    })
    // No marker, no reload: the marker is the only loop guard.
    if (reload && writeProReloadMarker(uid)) reloadPage()
  }, [loading, userId, status, isPro])
  return null
}
